import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  ArrowLeft, 
  Users, 
  Globe,
  Lock,
  Shield,
  Crown,
  Heart,
  Clock,
  UserPlus,
  Image as ImageIcon,
  X,
  Check
} from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';

export const GroupCreator: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'General Support',
    privacy: 'public' as 'public' | 'private' | 'invite_only',
    welcomeMessage: '',
    rules: ['Be respectful and supportive', 'No personal attacks or harassment', 'Keep discussions relevant to first responder wellness'],
    allowMemberPosts: true,
    allowMemberInvites: true,
  });

  const [groupImage, setGroupImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const categories = [
    { id: 'General Support', icon: Users, color: 'from-blue-500 to-blue-600', description: 'General peer support' },
    { id: 'Stress Management', icon: Heart, color: 'from-pink-500 to-rose-500', description: 'Coping & stress relief' },
    { id: 'Shift Workers', icon: Clock, color: 'from-purple-500 to-indigo-500', description: 'Irregular hours support' },
    { id: 'Leadership', icon: Crown, color: 'from-yellow-500 to-orange-500', description: 'Leadership development' },
    { id: 'Family Support', icon: UserPlus, color: 'from-green-500 to-emerald-500', description: 'Work-life balance' },
    { id: 'Peer Support', icon: Users, color: 'from-teal-500 to-cyan-500', description: 'Peer mentoring' }
  ];

  const privacyOptions = [
    { 
      id: 'public', 
      icon: Globe, 
      title: 'Public', 
      description: 'Anyone can discover and join',
      color: 'from-green-500 to-emerald-500'
    },
    { 
      id: 'invite_only', 
      icon: Shield, 
      title: 'Invite Only', 
      description: 'Join through invitations',
      color: 'from-blue-500 to-indigo-500'
    },
    { 
      id: 'private', 
      icon: Lock, 
      title: 'Private', 
      description: 'Completely private, not discoverable',
      color: 'from-gray-500 to-gray-600'
    }
  ];

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setGroupImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!user) {
      alert('You must be logged in to create a group.');
      return;
    }

    if (!formData.name.trim()) {
      alert('Group name is required.');
      return;
    }

    if (!formData.description.trim()) {
      alert('Group description is required.');
      return;
    }

    setLoading(true);
    
    try {
      let imageUrl: string | undefined;

      // Upload group image if selected
      if (groupImage) {
        const imageRef = ref(storage, `groupImages/${Date.now()}_${groupImage.name}`);
        await uploadBytes(imageRef, groupImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Create group document
      const groupData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        privacy: formData.privacy,
        welcomeMessage: formData.welcomeMessage.trim() || null,
        rules: formData.rules.filter(r => r.trim()),
        allowMemberPosts: formData.allowMemberPosts,
        allowMemberInvites: formData.allowMemberInvites,
        groupImage: imageUrl || null,
        leaderId: user.uid,
        memberCount: 1,
        postCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
      };

      const groupRef = await addDoc(collection(db, 'groups'), groupData);

      // Create leader membership
      const membershipData = {
        userId: user.uid,
        groupId: groupRef.id,
        role: 'leader' as const,
        canPost: true,
        canInvite: true,
        joinedAt: serverTimestamp(),
        status: 'active' as const,
      };

      await setDoc(doc(db, 'groupMemberships', `${user.uid}_${groupRef.id}`), membershipData);

      // Navigate to new group
      navigate(`/community/groups/${groupRef.id}`);
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group. Please try again.');
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.name.trim() && formData.description.trim();
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => step === 1 ? navigate('/community') : setStep((step - 1) as 1 | 2)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                disabled={loading}
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Create Group</h1>
                <p className="text-xs text-gray-600">Step {step} of 3</p>
              </div>
            </div>
            {step === 3 ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Create</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={() => setStep((step + 1) as 2 | 3)}
                disabled={!canProceed()}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                Next
              </button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mt-3 flex items-center space-x-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all ${
                  s <= step ? 'bg-gradient-to-r from-blue-500 to-purple-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Group Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Night Shift Support"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={50}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.name.length}/50 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what your group is about..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Category
              </label>
              <div className="grid grid-cols-2 gap-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setFormData({ ...formData, category: category.id })}
                    className={`p-4 rounded-2xl border-2 transition-all text-left ${
                      formData.category === category.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-10 h-10 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-2`}>
                      <category.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{category.id}</h3>
                    <p className="text-xs text-gray-600">{category.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Privacy & Settings */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Privacy Settings
              </label>
              <div className="space-y-3">
                {privacyOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setFormData({ ...formData, privacy: option.id as any })}
                    className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
                      formData.privacy === option.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-12 h-12 bg-gradient-to-br ${option.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <option.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">{option.title}</h3>
                        <p className="text-xs text-gray-600">{option.description}</p>
                      </div>
                      {formData.privacy === option.id && (
                        <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Member Permissions
              </label>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Allow Member Posts</h4>
                    <p className="text-xs text-gray-600">Members can create posts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.allowMemberPosts}
                    onChange={(e) => setFormData({ ...formData, allowMemberPosts: e.target.checked })}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Allow Member Invites</h4>
                    <p className="text-xs text-gray-600">Members can invite others</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.allowMemberInvites}
                    onChange={(e) => setFormData({ ...formData, allowMemberInvites: e.target.checked })}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Customization */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Group Image (Optional)
              </label>
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-2xl" />
                  <button
                    onClick={() => {
                      setGroupImage(null);
                      setImagePreview(null);
                    }}
                    className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="block w-full h-48 border-2 border-dashed border-gray-300 rounded-2xl hover:border-blue-500 transition-colors cursor-pointer">
                  <div className="h-full flex flex-col items-center justify-center text-gray-500">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <span className="text-sm font-medium">Tap to upload image</span>
                    <span className="text-xs text-gray-400 mt-1">Max 5MB</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Welcome Message (Optional)
              </label>
              <textarea
                value={formData.welcomeMessage}
                onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                placeholder="Write a welcome message for new members..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                maxLength={300}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.welcomeMessage.length}/300 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Group Rules
              </label>
              <div className="space-y-2">
                {formData.rules.map((rule, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-2.5">
                      <span className="text-white text-xs font-bold">{index + 1}</span>
                    </div>
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => {
                        const newRules = [...formData.rules];
                        newRules[index] = e.target.value;
                        setFormData({ ...formData, rules: newRules });
                      }}
                      placeholder={`Rule ${index + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {formData.rules.length > 1 && (
                      <button
                        onClick={() => {
                          const newRules = formData.rules.filter((_, i) => i !== index);
                          setFormData({ ...formData, rules: newRules });
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {formData.rules.length < 10 && (
                <button
                  onClick={() => setFormData({ ...formData, rules: [...formData.rules, ''] })}
                  className="mt-3 w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-xl hover:border-blue-500 hover:text-blue-600 transition-colors text-sm font-medium"
                >
                  + Add Rule
                </button>
              )}
            </div>

            {/* Preview Card */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Preview</h3>
              <div className="bg-white rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      <Users className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate">{formData.name || 'Group Name'}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2">{formData.description || 'Group description'}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-xs text-gray-500">{formData.category}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500 capitalize">{formData.privacy}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

